import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getApiUrl } from '../lib/api';
import { springs } from '../lib/animations';
import ScoreBar from '../components/ScoreBar';
import RollingTips from '../components/RollingTips';
import AnimatedButton from '../components/AnimatedButton';
import AnimatedCounter from '../components/AnimatedCounter';
import TypewriterText from '../components/TypewriterText';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import confetti from 'canvas-confetti';
import html2canvas from 'html2canvas';
import NamePromptModal from '../components/NamePromptModal';

interface Strain {
  name: string;
  type?: string;
  effects?: string[];
  flavors?: string[];
  description?: string;
  rating?: number;
}

interface StrainContext {
  name: string;
  type: string | null;
  effects: string | null;
  flavors: string | null;
  description: string | null;
}

interface RatingResult {
  id: number;
  roll_consistency: number;
  shape_taper: number;
  filter_crutch: number;
  seal_quality: number;
  aesthetics: number;
  final_score: number;
  verdict: string;
  image_path: string;
  estimated_length_cm?: number;
  estimated_thickness_mm?: number;
  tips?: string[] | string;
  strain?: StrainContext | null;
}

export default function Rate() {
  const { toasts, addToast } = useToast();
  const [showNameModal, setShowNameModal] = useState(false);
  const [rollerName, setRollerName] = useState('');
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const sessionName = localStorage.getItem('rollerName') || '';
  const [preview, setPreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RatingResult | null>(null);
  const [error, setError] = useState('');
  const [showScores, setShowScores] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgRemovalFailed, setBgRemovalFailed] = useState(false);
  const [strains, setStrains] = useState<Strain[]>([]);
  const [strainSearch, setStrainSearch] = useState('');
  const [selectedStrain, setSelectedStrain] = useState<Strain | null>(null);
  const [strainDropdownOpen, setStrainDropdownOpen] = useState(false);
  const [strainsLoading, setStrainsLoading] = useState(false);
  const strainRef = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const resultCard = useRef<HTMLDivElement>(null);

  // Fetch strains on mount
  useEffect(() => {
    setStrainsLoading(true);
    fetch(getApiUrl('/api/strains-data?per_page=500'))
      .then(r => r.json())
      .then(data => {
        const items = data.items ?? data;
        if (Array.isArray(items)) setStrains(items);
      })
      .catch((err) => {
        console.error('Failed to load strains:', err);
        addToast('Failed to load strain data', 'warning');
      })
      .finally(() => setStrainsLoading(false));
  }, [addToast]);

  // Close strain dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (strainRef.current && !strainRef.current.contains(e.target as Node)) {
        setStrainDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (result) {
      setTimeout(() => setShowScores(true), 100);
      if (result.final_score >= 8) {
        setTimeout(() => confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } }), 500);
      }
    }
  }, [result]);

  const filteredStrains = strains.filter(s =>
    (typeof s.name === 'string' ? s.name.toLowerCase() : '').includes(strainSearch.toLowerCase())
  ).slice(0, 50);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setShowScores(false);

    // Show original preview immediately
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
      setImageBase64(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Try background removal (optional feature)
    try {
      setRemovingBg(true);
      setBgRemovalFailed(false);
      const { removeBackground } = await import('@imgly/background-removal');
      const blob = await removeBackground(file, {
        publicPath: 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.4.5/dist/',
      });
      const bgReader = new FileReader();
      bgReader.onload = () => {
        const base64 = bgReader.result as string;
        setPreview(base64);
        setImageBase64(base64);
        setRemovingBg(false);
      };
      bgReader.readAsDataURL(blob);
    } catch {
      setBgRemovalFailed(true);
      setRemovingBg(false);
    }
  };

  const handleRate = async () => {
    if (!imageBase64) return;
    const finalName = nameConfirmed ? rollerName : sessionName;
    if (!finalName) {
      setShowNameModal(true);
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setShowScores(false);

    try {
      const res = await fetch(getApiUrl('/api/rate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rollerName: finalName,
          imageBase64,
          strainName: selectedStrain?.name || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Rating failed');
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setImageBase64('');
    setResult(null);
    setShowScores(false);
    setError('');
    setSelectedStrain(null);
    setStrainSearch('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleShare = async () => {
    if (!resultCard.current) return;
    try {
      const canvas = await html2canvas(resultCard.current, { backgroundColor: '#1a1f16', scale: 2 });
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return;

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'joint-rating.png', { type: 'image/png' })] })) {
        await navigator.share({
          title: 'Hashups',
          text: `${rollerName || sessionName} scored ${result?.final_score.toFixed(2)}!`,
          url: `${window.location.origin}/rating/${result?.id}`,
          files: [new File([blob], 'joint-rating.png', { type: 'image/png' })],
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'joint-rating.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      // User cancelled share
    }
  };

  const displayName = nameConfirmed ? rollerName : sessionName;

  // Name input step
  if (!nameConfirmed) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-cream mb-2 text-center">What's your name?</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={rollerName}
            onChange={e => setRollerName(e.target.value)}
            placeholder="Enter your name..."
            className="flex-1 bg-cannabis-800 border border-cannabis-600 rounded-lg px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400"
            onKeyDown={e => e.key === 'Enter' && rollerName.trim() && setNameConfirmed(true)}
            autoFocus
          />
          <AnimatedButton
            onClick={() => rollerName.trim() && setNameConfirmed(true)}
            disabled={!rollerName.trim()}
          >
            Go
          </AnimatedButton>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <div className="mb-1">
        <h2 className="text-2xl font-bold text-cream">Rate a Joint</h2>
      </div>
      <p className="text-cream/50 text-sm mb-6">Rating as <span className="text-cannabis-400 font-semibold">{displayName}</span></p>

      {!result ? (
        <div className="space-y-6">
          {/* Strain Selection */}
          <div ref={strainRef} className="relative">
            <label className="block text-cream/60 text-sm mb-1">Select Strain (Optional)</label>
            <div
              className="bg-cannabis-800 border border-cannabis-600 rounded-lg px-4 py-3 text-cream cursor-pointer flex items-center justify-between"
              onClick={() => setStrainDropdownOpen(!strainDropdownOpen)}
            >
              {selectedStrain ? (
                <span className="flex items-center gap-2">
                  <span>{selectedStrain.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    selectedStrain.type === 'Sativa' ? 'bg-yellow-500/20 text-yellow-300' :
                    selectedStrain.type === 'Indica' ? 'bg-purple-500/20 text-purple-300' :
                    'bg-cannabis-500/20 text-cannabis-300'
                  }`}>{selectedStrain.type || 'Hybrid'}</span>
                </span>
              ) : (
                <span className="text-cream/30">{strainsLoading ? 'Loading strains...' : 'Search strains...'}</span>
              )}
              {selectedStrain ? (
                <button onClick={(e) => { e.stopPropagation(); setSelectedStrain(null); setStrainSearch(''); }} className="text-cream/40 hover:text-cream text-lg">×</button>
              ) : (
                <span className="text-cream/30 text-sm">▼</span>
              )}
            </div>
            {strainDropdownOpen && (
              <div className="absolute z-40 w-full mt-1 bg-cannabis-800 border border-cannabis-600 rounded-lg shadow-xl max-h-64 overflow-hidden">
                <div className="p-2 border-b border-cannabis-700">
                  <input
                    type="text"
                    value={strainSearch}
                    onChange={e => setStrainSearch(e.target.value)}
                    placeholder="Type to search..."
                    className="w-full bg-cannabis-900 border border-cannabis-700 rounded px-3 py-2 text-cream text-sm placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400"
                    autoFocus
                  />
                </div>
                <div className="overflow-y-auto max-h-48">
                  {filteredStrains.length === 0 ? (
                    <div className="px-4 py-3 text-cream/30 text-sm text-center">No strains found</div>
                  ) : (
                    filteredStrains.map((strain) => (
                      <button
                        key={strain.name}
                        className="w-full text-left px-4 py-2.5 hover:bg-cannabis-700 transition flex items-center justify-between"
                        onClick={() => {
                          setSelectedStrain(strain);
                          setStrainDropdownOpen(false);
                          setStrainSearch('');
                        }}
                      >
                        <span className="text-cream text-sm">{strain.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          strain.type === 'Sativa' ? 'bg-yellow-500/20 text-yellow-300' :
                          strain.type === 'Indica' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-cannabis-500/20 text-cannabis-300'
                        }`}>{strain.type || 'Hybrid'}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <motion.div
            className="border-2 border-dashed rounded-xl p-6 text-center relative overflow-hidden"
            animate={!preview ? { borderColor: ['rgba(78,124,58,0.3)', 'rgba(78,124,58,0.6)', 'rgba(78,124,58,0.3)'] } : {}}
            transition={!preview ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : {}}
            style={{ borderColor: preview ? 'rgb(78,124,58)' : undefined }}
          >
            {preview ? (
              <div className="relative inline-block mb-4">
                <img src={preview} alt="Joint preview" className="max-h-64 mx-auto rounded-lg" />
                {removingBg && (
                  <div className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center gap-2">
                    <motion.span
                      className="text-2xl inline-block"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      ...
                    </motion.span>
                    <span className="text-cream text-sm">Removing background...</span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-cream/30 text-sm mb-4">Upload a photo</div>
            )}

            <div className="flex gap-3">
              <AnimatedButton
                onClick={() => cameraInput.current?.click()}
                variant="secondary"
                className="flex-1 flex items-center justify-center gap-2"
              >
                Camera
              </AnimatedButton>
              <AnimatedButton
                onClick={() => fileInput.current?.click()}
                variant="secondary"
                className="flex-1 flex items-center justify-center gap-2"
              >
                Gallery
              </AnimatedButton>
            </div>

            <input ref={cameraInput} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
            <input ref={fileInput} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </motion.div>

          {bgRemovalFailed && (
            <p className="text-yellow-400 text-sm text-center">Background removal failed</p>
          )}

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          <AnimatedButton
            onClick={handleRate}
            disabled={!imageBase64 || loading}
            loading={loading}
            size="lg"
            className="w-full"
          >
            {loading ? 'The connoisseur is judging...' : 'Rate This Joint'}
          </AnimatedButton>
        </div>
      ) : (
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={springs.dramatic}
        >
          {/* Shareable result card */}
          <div ref={resultCard} className="bg-cannabis-800 rounded-xl p-6 border border-cannabis-700">
            <div className="text-center text-cannabis-400 text-xs font-bold mb-3 tracking-widest uppercase">Hashups</div>
            {preview && (
              <img src={preview} alt="Rated joint" className="max-h-48 mx-auto rounded-lg mb-4" />
            )}
            <div className="text-center mb-1 text-cream/40 text-xs">{displayName}</div>
            {result.strain && (
              <div className="flex justify-center mb-3">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  result.strain.type === 'Sativa' ? 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/30' :
                  result.strain.type === 'Indica' ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30' :
                  'bg-cannabis-500/15 text-cannabis-300 border border-cannabis-500/30'
                }`}>
                  <span>{result.strain.name}</span>
                  <span className="text-xs opacity-70">({result.strain.type || 'Hybrid'})</span>
                </div>
              </div>
            )}
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-cannabis-300">{result.final_score.toFixed(2)} <span className="text-2xl text-cream/40">/ 10</span></div>
              <div className="text-cream/40 text-sm">Final Score</div>
              {(result.estimated_length_cm || result.estimated_thickness_mm) && (
                <div className="text-cream/50 text-xs mt-1">
                  Estimated Length: {result.estimated_length_cm ?? '?'} cm | Estimated Thickness: {result.estimated_thickness_mm ?? '?'} mm
                </div>
              )}
            </div>

            <div className="space-y-3">
              <ScoreBar label="Roll Consistency" score={showScores ? result.roll_consistency : 0} />
              <ScoreBar label="Shape & Taper" score={showScores ? result.shape_taper : 0} />
              <ScoreBar label="Filter/Crutch" score={showScores ? result.filter_crutch : 0} />
              <ScoreBar label="Seal Quality" score={showScores ? result.seal_quality : 0} />
              <ScoreBar label="Aesthetics" score={showScores ? result.aesthetics : 0} />
            </div>
          </div>

          <div className="bg-cannabis-800/50 rounded-xl p-5 border border-cannabis-700/50">
            <p className="text-cream/90 italic text-lg leading-relaxed">
              "<TypewriterText text={result.verdict} />"
            </p>
            {result.strain && (
              <div className="mt-3 pt-3 border-t border-cannabis-700/50">
                <p className="text-cannabis-400 text-sm">
                  For a {result.strain.type || 'hybrid'} like {result.strain.name}, expect: {result.strain.effects || 'varied effects'}
                </p>
              </div>
            )}
          </div>

          {/* Rolling improvement tips */}
          {(() => {
            let tips: string[] = [];
            if (Array.isArray(result.tips)) {
              tips = result.tips;
            } else if (typeof result.tips === 'string') {
              try { tips = JSON.parse(result.tips); } catch { tips = []; }
            }
            return tips.length > 0 ? <RollingTips tips={tips} /> : null;
          })()}

          {/* Feedback component for improving AI ratings */}
          <div className="bg-cannabis-800/50 rounded-xl p-5 border border-cannabis-700/50">
            <p className="text-cream/60 text-sm font-medium mb-2">Help the AI learn from this rating:</p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  // Send feedback that joint is actually great
                  fetch(getApiUrl('/api/feedback'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      imageId: result?.id,
                      feedbackType: 'joint_actually_great'
                    })
                  }).then(res => res.json()).then(data => {
                    if (data.success) {
                      alert('Thanks! Your feedback helps the AI learn what makes a great joint.');
                    }
                  }).catch(err => {
                    console.error('Feedback error:', err);
                    alert('Failed to send feedback');
                  });
                }}
                className="w-full bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition"
              >
                This Joint is Actually Great!
              </button>
              
              <button
                onClick={() => {
                  // Send feedback that joint is actually terrible
                  fetch(getApiUrl('/api/feedback'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      imageId: result?.id,
                      feedbackType: 'joint_actually_terrible'
                    })
                  }).then(res => res.json()).then(data => {
                    if (data.success) {
                      alert('Thanks! Your feedback helps the AI learn what needs improvement.');
                    }
                  }).catch(err => {
                    console.error('Feedback error:', err);
                    alert('Failed to send feedback');
                  });
                }}
                className="w-full bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
              >
                This Joint is Actually Terrible!
              </button>
              
              <button
                onClick={() => {
                  // Ask what the AI missed
                  const missed = window.prompt("What did the AI miss about this joint?\nExamples: uneven burning, poor ash stability, draw issues, bad smell/taste, visible mold, stem/seeds, moisture issues");
                  if (missed) {
                    fetch(getApiUrl('/api/feedback'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        imageId: result?.id,
                        feedbackType: 'ai_missed_something',
                        details: missed
                      })
                    }).then(res => res.json()).then(data => {
                      if (data.success) {
                        alert('Thanks! Your specific feedback helps the AI improve its analysis.');
                      }
                    }).catch(err => {
                      console.error('Feedback error:', err);
                      alert('Failed to send feedback');
                    });
                  }
                }}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
              >
                AI Missed Something Specific
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <AnimatedButton onClick={reset} variant="secondary" className="flex-1">
              Rate Another
            </AnimatedButton>
            <AnimatedButton onClick={handleShare} className="flex-1">
              Share
            </AnimatedButton>
          </div>
        </motion.div>
      )}
      <NamePromptModal
        open={showNameModal}
        onClose={() => setShowNameModal(false)}
        onSubmit={() => { setShowNameModal(false); handleRate(); }}
      />
      <Toast toasts={toasts} />
    </div>
  );
}